import React, { useEffect, useState } from "react";
import { Button } from "../components/ui/button";
import { CheckCircle2, Clock, XCircle, CheckSquare, RotateCcw, Loader2 } from "lucide-react";
import BookingActionModal from "./BookingActionModal";
import { useAdminAuth } from "./AuthContext";
import { useToast } from "../hooks/use-toast";

const TERMINAL = new Set(["completed", "declined", "cancelled"]);

export default function BookingActionButtons({ booking, onUpdated, size = "sm" }) {
  const { authAxios } = useAdminAuth();
  const { toast } = useToast();
  const [modal, setModal] = useState({ open: false, action: null });
  const [reopening, setReopening] = useState(false);

  // Hold the latest booking locally so this component re-renders immediately
  // after an action mutates the booking, without depending on the parent
  // propagating the updated prop back to us.
  const [currentBooking, setCurrentBooking] = useState(booking);
  useEffect(() => {
    setCurrentBooking(booking);
  }, [booking]);

  if (!currentBooking) return null;
  const isTerminal = TERMINAL.has(currentBooking.status);

  const openAction = (action) => setModal({ open: true, action });

  const handleCompleted = (updated) => {
    setCurrentBooking(updated);
    onUpdated?.(updated);
  };

  const reopen = async () => {
    setReopening(true);
    try {
      const r = await authAxios.post(`/bookings/${currentBooking.id}/reopen`);
      toast({ title: "Booking reopened" });
      setCurrentBooking(r.data);
      onUpdated?.(r.data);
    } catch (e) {
      toast({
        title: "Reopen failed",
        description: e?.response?.data?.detail || "Server error",
        variant: "destructive",
      });
    } finally {
      setReopening(false);
    }
  };

  return (
    <>
      <div className="flex flex-wrap gap-2" data-testid="booking-action-buttons">
        <Button
          onClick={() => openAction("confirm")}
          disabled={isTerminal || booking.status === "confirmed"}
          className="bg-emerald-600 hover:bg-emerald-700 text-white disabled:bg-slate-200 disabled:text-slate-400"
          size={size}
          data-testid="confirm-booking-btn"
        >
          <CheckCircle2 size={14} className="mr-1" /> Confirm
        </Button>
        <Button
          onClick={() => openAction("reschedule")}
          variant="outline"
          size={size}
          disabled={isTerminal}
          className="border-amber-300 text-amber-700 hover:bg-amber-50 disabled:opacity-50 disabled:hover:bg-transparent"
          data-testid="reschedule-booking-btn"
        >
          <Clock size={14} className="mr-1" /> Reschedule
        </Button>
        <Button
          onClick={() => openAction("decline")}
          variant="outline"
          size={size}
          disabled={isTerminal}
          className="border-rose-300 text-rose-700 hover:bg-rose-50 disabled:opacity-50 disabled:hover:bg-transparent"
          data-testid="decline-booking-btn"
        >
          <XCircle size={14} className="mr-1" /> Decline
        </Button>
        <Button
          onClick={() => openAction("complete")}
          size={size}
          disabled={isTerminal}
          className="bg-slate-700 hover:bg-slate-800 text-white disabled:bg-slate-200 disabled:text-slate-400"
          data-testid="complete-booking-btn"
        >
          <CheckSquare size={14} className="mr-1" /> Complete
        </Button>
        {isTerminal && (
          <Button
            onClick={reopen}
            disabled={reopening}
            size={size}
            className="bg-sky-600 hover:bg-sky-700 text-white"
            data-testid="reopen-booking-btn"
          >
            {reopening ? (
              <Loader2 className="animate-spin mr-1" size={14} />
            ) : (
              <RotateCcw size={14} className="mr-1" />
            )}
            Reopen
          </Button>
        )}
      </div>

      <BookingActionModal
        open={modal.open}
        action={modal.action}
        booking={currentBooking}
        onOpenChange={(v) => setModal((m) => ({ ...m, open: v }))}
        onCompleted={handleCompleted}
      />
    </>
  );
}
